/**
 * شاشة الدخول — لوحان (design/Login.dc.html).
 *
 * §5 من ملحق القرارات: الحقل الأول **«البريد أو اسم المستخدم»** واسمه
 * `identifier` — مش `email`. `username` هو المفتاح الإجباري للدخول (§6).
 *
 * ⛔ مفيش زر «اسأل المساعد» في الشاشة دي.
 */
import { useState, type FormEvent } from "react";
import { Icon } from "@/components/Icon";
import { PasswordReveal } from "@/components/PasswordReveal";
import { errorMessageAr, ApiError, OfflineError } from "@/api/client";
import { forgotPassword } from "@/api/auth";
import { useSession } from "@/lib/session";
import { Link, REGISTER_PATH } from "@/lib/router";
import "@/styles/auth.css";

export function Login() {
  const { signIn, notice, clearNotice } = useSession();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"login" | "forgot">("login");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    if (!identifier.trim() || !password) {
      setError("لازم تكتب البريد أو اسم المستخدم وكلمة السر");
      return;
    }

    setBusy(true);
    setError(null);
    clearNotice();
    try {
      await signIn(identifier.trim(), password, remember);
      setPassword("");
      // ⚠️ ممنوع نوجّه لـ«/desk» من هنا: مين اللي داخل بيحدّد شاشته —
      //    الحساب المعلّق بيروح «تحت المراجعة» مش الشِل. القرار كله في
      //    `App.tsx` من مصدر واحد، وإلا الحساب المعلّق بيلمح الشِل لحظة.
    } catch (err) {
      // ⚠️ الرسالة بتيجي من السيستم زي ما هي («فاضل 3 محاولات» · «الحساب اتقفل مؤقتًا»)
      //    الواجهة ما بتصيغش نص من عندها طالما السيرفر بعت واحد.
      setError(errorMessageAr(err));
      if (!(err instanceof ApiError) && !(err instanceof OfflineError)) {
        console.error("[login] فشل مش متوقع", err);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth" dir="rtl">
      {/* ================= (أ) اليمين — النموذج ================= */}
      <section className="auth-pane form">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="brand-mark">
              <Icon name="book" size={20} stroke="#0F1D33" />
            </div>
            <div>
              <div className="brand-name">رتّل</div>
              <div className="brand-sub">السيستم الإداري</div>
            </div>
          </div>

          {mode === "login" ? (
            <LoginForm
              notice={notice}
              identifier={identifier}
              password={password}
              remember={remember}
              busy={busy}
              error={error}
              onIdentifier={setIdentifier}
              onPassword={setPassword}
              onRemember={setRemember}
              onSubmit={onSubmit}
              onForgot={() => {
                setError(null);
                clearNotice();
                setMode("forgot");
              }}
            />
          ) : (
            <ForgotForm
              onBack={() => {
                setError(null);
                setMode("login");
              }}
            />
          )}
        </div>
      </section>

      <AuthPitch />
    </div>
  );
}

/* ═══════════ لوح التعريف الكحلي — مشترك مع شاشة التسجيل ═══════════
   شاشة التسجيل «نفس تخطيط الدخول بلوحين» (ملحق التسجيل الذاتي)،
   فاللوح ده واحد في الاتنين بلا نسخ. */

export function AuthPitch() {
  return (
    <section className="auth-pane pitch">
      <div className="pitch-box">
        <div className="pitch-title">رتّل — السيستم الإداري</div>
        <div className="pitch-sub" style={{ marginTop: 12 }}>
          كل مهامك ورحلاتك وسجلاتك في مكان واحد
        </div>

        <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="feat">
            <span className="feat-mark">
              <Icon name="shield" size={18} stroke="#FFFFFF" />
            </span>
            كل واحد يشوف اللي عليه بس
          </div>
          <div className="feat">
            <span className="feat-mark">
              <Icon name="journey" size={18} stroke="#FFFFFF" />
            </span>
            الخطوة تتقفل وتسلّم للي بعدها لوحدها
          </div>
          <div className="feat">
            <span className="feat-mark">
              <Icon name="bell" size={18} stroke="#FFFFFF" />
            </span>
            إشعار على تليجرام لما ييجي دورك
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────── نموذج الدخول ─────────────────────── */

function LoginForm(props: {
  /** رسالة جاية من جلسة سابقة — مثلًا خروج ما وصلش للسيستم */
  notice: string | null;
  identifier: string;
  password: string;
  remember: boolean;
  busy: boolean;
  error: string | null;
  onIdentifier: (v: string) => void;
  onPassword: (v: string) => void;
  onRemember: (v: boolean) => void;
  onSubmit: (e: FormEvent) => void;
  onForgot: () => void;
}) {
  const [showPw, setShowPw] = useState(false);
  return (
    <form onSubmit={props.onSubmit} noValidate className="auth-form">
      <div style={{ marginTop: 32 }}>
        <div className="auth-title">أهلًا بحضرتك</div>
        <div className="t-desc" style={{ marginTop: 8 }}>
          تسجيل الدخول لعرض مهام النهارده
        </div>
      </div>

      {props.notice && !props.error ? (
        // ⚠️ الخروج اللي ما وصلش للسيستم لازم يوصل للموظف — مش يعدّي صامت
        <div
          className="err-box"
          style={{ marginTop: 20, background: "var(--warn-bg)", borderColor: "#F5D9A8" }}
          role="status"
          aria-live="polite"
        >
          <Icon name="alert" size={18} stroke="#B54708" />
          <span className="t-body" style={{ color: "var(--warn)" }}>
            {props.notice}
          </span>
        </div>
      ) : null}

      {props.error ? (
        <div className="err-box" style={{ marginTop: 20 }} role="alert" aria-live="assertive">
          <Icon name="alert" size={18} stroke="#B42318" />
          <span className="t-body" style={{ color: "var(--danger)" }}>
            {props.error}
          </span>
        </div>
      ) : null}

      <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label className="field-label" htmlFor="identifier">
            البريد أو اسم المستخدم
          </label>
          <div className="input" style={{ marginTop: 8 }}>
            <Icon name="mail" size={16} stroke="#98A2B3" />
            {/* dir="ltr" هنا مسموح: بريد أو اسم مستخدم لاتيني صافي */}
            <input
              id="identifier"
              name="identifier"
              type="text"
              dir="ltr"
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={props.identifier}
              onChange={(e) => props.onIdentifier(e.target.value)}
              disabled={props.busy}
            />
          </div>
        </div>

        <div>
          <label className="field-label" htmlFor="password">
            كلمة السر
          </label>
          <div className="input" style={{ marginTop: 8 }}>
            <Icon name="lock" size={16} stroke="#98A2B3" />
            <input
              id="password"
              name="password"
              type={showPw ? "text" : "password"}
              dir="ltr"
              autoComplete="current-password"
              value={props.password}
              onChange={(e) => props.onPassword(e.target.value)}
              disabled={props.busy}
            />
            <PasswordReveal shown={showPw} onToggle={() => setShowPw((v) => !v)} disabled={props.busy} />
          </div>
        </div>
      </div>

      <div className="auth-remember" style={{ marginTop: 16 }}>
        <input
          id="remember"
          type="checkbox"
          className="checkbox"
          checked={props.remember}
          onChange={(e) => props.onRemember(e.target.checked)}
          disabled={props.busy}
        />
        <label
          htmlFor="remember"
          className="t-body"
          style={{ fontWeight: 400, color: "var(--ink-2)", cursor: "pointer" }}
        >
          فكّرني
        </label>
        <button
          type="button"
          className="link"
          style={{ marginRight: "auto" }}
          onClick={props.onForgot}
        >
          نسيت كلمة السر؟
        </button>
      </div>

      <button
        type="submit"
        className={`btn primary${props.busy ? " is-busy" : ""}`}
        style={{ marginTop: 20, height: 48 }}
        disabled={props.busy}
      >
        {props.busy ? (
          <>
            <Icon name="spinner" size={16} stroke="#FFFFFF" strokeWidth={1.9} />
            بيدخّل…
          </>
        ) : (
          "دخول"
        )}
      </button>

      {/* ملحق التسجيل الذاتي §المسار-١: السطر ده هو باب التسجيل الوحيد */}
      <div className="t-body" style={{ marginTop: 16, textAlign: "center", fontWeight: 400 }}>
        ما عنديش حساب —{" "}
        <Link to={REGISTER_PATH} className="link">
          سجّل
        </Link>
      </div>

      <div className="t-meta" style={{ marginTop: 10, textAlign: "center" }}>
        الدخول باسم كل موظف — والصلاحيات حسب قسمه
      </div>
      <div className="t-meta" style={{ marginTop: 6, textAlign: "center" }}>
        مشكلة في الدخول؟ برجاء التواصل مع إدارة الأكاديمية
      </div>
    </form>
  );
}

/* ─────────────────── استعادة كلمة السر ───────────────────
   الرد 202 دايمًا — ما بنكشفش إن الحساب موجود ولا لأ. */

function ForgotForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!email.trim()) {
      setError("اكتب البريد المسجّل في الأكاديمية");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setDone(true);
    } catch (err) {
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="auth-form">
      <div style={{ marginTop: 32 }}>
        <div className="auth-title">استعادة كلمة السر</div>
        <div className="t-desc" style={{ marginTop: 8 }}>
          هنبعت خطوات تغيير كلمة السر على بريدك المسجّل
        </div>
      </div>

      {error ? (
        <div className="err-box" style={{ marginTop: 20 }} role="alert">
          <Icon name="alert" size={18} stroke="#B42318" />
          <span className="t-body" style={{ color: "var(--danger)" }}>
            {error}
          </span>
        </div>
      ) : null}

      {done ? (
        <div className="err-box" style={{ marginTop: 20, background: "var(--ok-bg)", borderColor: "#BBF7D0" }}>
          <Icon name="check" size={18} stroke="#067647" strokeWidth={2.2} />
          <span className="t-body" style={{ color: "var(--ok)" }}>
            لو البريد ده مسجّل عندنا، هتوصلك الخطوات خلال دقايق
          </span>
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          <label className="field-label" htmlFor="forgot-email">
            البريد
          </label>
          <div className="input" style={{ marginTop: 8 }}>
            <Icon name="mail" size={16} stroke="#98A2B3" />
            <input
              id="forgot-email"
              type="email"
              dir="ltr"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>
      )}

      {!done ? (
        <button
          type="submit"
          className={`btn primary${busy ? " is-busy" : ""}`}
          style={{ marginTop: 20, height: 48 }}
          disabled={busy}
        >
          {busy ? "بيبعت…" : "إرسال الخطوات"}
        </button>
      ) : null}

      <button type="button" className="link" style={{ marginTop: 14, textAlign: "center" }} onClick={onBack}>
        رجوع لتسجيل الدخول
      </button>

      <div className="t-meta" style={{ marginTop: 10, textAlign: "center" }}>
        مشكلة في الدخول؟ برجاء التواصل مع إدارة الأكاديمية
      </div>
    </form>
  );
}
